import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { z } from 'zod';
import { fetchJson } from '../../../lib/apiFetchJson';
import { writeMayHaveLanded } from '../../../lib/writeMayHaveLanded';
import {
  TrackerRecordSchema,
  TrackerUpsertBodySchema,
  type TrackerEntity,
  type TrackerRecord,
  type TrackerStatus,
} from '@shared/flow/tracker';
import { invalidateFlowList } from '../flow-list-invalidation';

/**
 * Tracker write API (U7 / R6) — wraps `PUT`/`DELETE /api/flow/tracker/:entity/:recordId`
 * for `TrackerChip`/`TrackerPopover`.
 *
 * Cache-indefinitely + invalidate-on-mutation (project rule, Decision 13):
 * the tracker rides ORDINARY fields on the list rows via a `LEFT JOIN
 * flow_record_trackers` (both list queries — `src/api/flow.ts`), not a
 * fetch of its own, so there is no separate "tracker cache" to keep in
 * sync — invalidating the owning entity's list query
 * (`['flow-contacts', …]` / `['flow-accounts', …]`, matched by the
 * `['flow-contacts']`/`['flow-accounts']` PREFIX so every filter/sort/
 * groupBy variant currently cached gets refetched) plus the favorite counts
 * (`['flow-favorite-counts']`) is the whole surface.
 *
 * `PUT` always sends the WHOLE tracker state (the route upserts, it never
 * patches — see `flow-tracker.ts`'s doc comment), and `TrackerUpsertBodySchema.parse`
 * enforces the same date rule the server does (a starred+date or a dateless
 * snooze/reminder throws here instead of round-tripping a 400 — the popover
 * already disables Save until the rule is satisfied, so this is a defense-
 * in-depth check, not the primary guard).
 */

export interface SaveTrackerInput {
  recordId: string;
  status: TrackerStatus;
  /** `YYYY-MM-DD`, or null for `starred` (must be null — see the schema's date rule). */
  date: string | null;
  note: string | null;
}

export interface TrackerMutations {
  save: (input: SaveTrackerInput) => void;
  clear: (recordId: string) => void;
  isSaving: boolean;
  isClearing: boolean;
}

export function useTrackerMutations(entity: TrackerEntity): TrackerMutations {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  // A tracker status is a filterable field, so the list AND the favorite counts can move.
  const invalidate = useCallback(() => invalidateFlowList(queryClient, entity), [queryClient, entity]);

  const saveMutation = useMutation({
    mutationFn: async (input: SaveTrackerInput): Promise<TrackerRecord> => {
      const body = TrackerUpsertBodySchema.parse({
        trackerStatus: input.status,
        trackerDate: input.date,
        note: input.note,
      });
      return fetchJson(apiFetch, TrackerRecordSchema, `/api/flow/tracker/${entity}/${encodeURIComponent(input.recordId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: invalidate,
    // A malformed or aborted response may still have saved the tracker: refresh.
    onError: (err) => {
      if (writeMayHaveLanded(err)) void invalidate();
    },
  });

  const clearMutation = useMutation({
    mutationFn: async (recordId: string): Promise<void> => {
      // The route answers 204 with no body.
      await fetchJson(apiFetch, z.undefined(), `/api/flow/tracker/${entity}/${encodeURIComponent(recordId)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: invalidate,
  });

  const save = useCallback((input: SaveTrackerInput) => { saveMutation.mutate(input); }, [saveMutation]);
  const clear = useCallback((recordId: string) => { clearMutation.mutate(recordId); }, [clearMutation]);

  return useMemo(
    () => ({ save, clear, isSaving: saveMutation.isPending, isClearing: clearMutation.isPending }),
    [save, clear, saveMutation.isPending, clearMutation.isPending],
  );
}
